<template>
    <v-container fluid fill-height>
        <Headful title="Aanvraag beoordelen"></Headful>
        <Header color="white" show-logo :dark="false" extended></Header>
        <v-layout align-center justify-center>
            <v-flex>
                <p v-if="loading" class="text-center my-7">
                    <v-icon large color="primary">fal fa-spinner-third fa-spin</v-icon>
                </p>
                <v-alert v-else-if="notFound" dark color="error" class="text-center my-7">
                    Deze link is niet (meer) geldig.
                </v-alert>
                <div v-else class="px-5">
                    <v-card class="elevation-0 mx-auto" max-width="640">
                        <v-card-text class="pa-7">
                            <h1 class="text-h5 mb-5 text-center">Aanvraag beoordelen</h1>

                            <v-simple-table class="mb-5">
                                <tbody>
                                    <tr><td><strong>Naam</strong></td><td>{{ request.name }}</td></tr>
                                    <tr><td><strong>Functie</strong></td><td>{{ request.function }}</td></tr>
                                    <tr><td><strong>E-mail</strong></td><td>{{ request.email }}</td></tr>
                                    <tr><td><strong>Dienstverlener</strong></td><td>{{ request.organisationName }}</td></tr>
                                    <tr><td><strong>Type dienst</strong></td><td>{{ request.typeDienst }}</td></tr>
                                    <tr v-if="request.managerApproval"><td><strong>Toestemming leidinggevende</strong></td><td>Ja</td></tr>
                                    <tr v-if="request.organisationCosts"><td><strong>Kosten (incl. btw)</strong></td><td>{{ formattedCosts }}</td></tr>
                                    <tr v-if="request.motivation"><td><strong>Motivatie</strong></td><td>{{ request.motivation }}</td></tr>
                                </tbody>
                            </v-simple-table>

                            <!-- De bekostiging is gelijk voor alle typen diensten, dus dezelfde tekst voor iedere aanvraag. -->
                            <h2 class="text-subtitle-1 font-weight-bold mb-1">Bekostiging en vergoeding</h2>
                            <p class="mb-5">
                                Voor bekostiging van deze dienst heeft jullie bestuur al een vergoeding ontvangen vanuit
                                Onderwijsregio Drechtsteden/West-Brabant. Conform afspraak besteedt ieder deelnemend
                                bestuur &euro;50 per leerling aan professionalisering en begeleiding starters, door inzet
                                van in- of externe begeleiders of tools naar keuze.
                            </p>

                            <v-alert v-if="decided" :color="decidedColor" class="mb-0">
                                {{ decidedMessage }}
                            </v-alert>

                            <div v-else-if="!alreadyHandled">
                                <h2 class="text-subtitle-1 font-weight-bold mb-1">Facturatie kosten</h2>
                                <p class="mb-4">Vul de facturatiegegevens in. Deze zijn verplicht bij het goedkeuren van de aanvraag.</p>
                                <v-form ref="invoiceForm" :disabled="!!submitting">
                                    <v-text-field outlined rounded required hide-details="auto" class="mb-4"
                                        label="Naam rekeninghouder" v-model="invoice.accountHolder" :rules="[requiredRule]"
                                    ></v-text-field>
                                    <v-text-field outlined rounded required hide-details="auto" class="mb-4"
                                        label="IBAN-nummer" v-model="invoice.iban" :rules="[requiredRule, ibanRule]"
                                    ></v-text-field>
                                    <v-text-field outlined rounded hide-details="auto" class="mb-5"
                                        label="Uw referentie (optioneel)" v-model="invoice.reference"
                                    ></v-text-field>
                                </v-form>

                                <p class="text-center mb-4">Wat is je beoordeling?</p>
                                <v-btn color="success" rounded block class="mb-3 elevation-0" :loading="submitting === 'approved'" :disabled="!!submitting" @click="decide('approved')">
                                    Goedkeuren
                                </v-btn>
                                <v-btn color="info" rounded block class="mb-3 elevation-0" :loading="submitting === 'in_conversation'" :disabled="!!submitting" @click="decide('in_conversation')">
                                    In gesprek
                                </v-btn>
                                <v-btn color="error" rounded block class="elevation-0" :loading="submitting === 'denied'" :disabled="!!submitting" @click="decide('denied')">
                                    Afkeuren
                                </v-btn>
                            </div>

                            <v-alert v-else color="warning" class="mb-0">
                                Deze aanvraag is al beoordeeld.
                            </v-alert>
                        </v-card-text>
                    </v-card>
                </div>
            </v-flex>
        </v-layout>
    </v-container>
</template>

<script>
import Headful from "@/components/layout/Headful";
import Header from "@/components/layout/Header";
import {formatCosts} from "@/utils/formatters";

// Zelfde controle als Symfony's Iban-constraint in de API: globale opbouw (landcode, twee
// controlecijfers, 10 t/m 30 tekens) plus het mod-97-controlegetal.
function isValidIban(value) {
    const iban = value.replace(/\s+/g, '').toUpperCase();

    if (!iban.match(/^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/)) {
        return false;
    }

    // Eerste vier tekens naar achteren, letters als getal (A = 10 ... Z = 35); per cijfer
    // modulo 97 rekenen, zodat het getal niet te groot wordt.
    const digits = (iban.slice(4) + iban.slice(0, 4)).replace(/[A-Z]/g, letter => letter.charCodeAt(0) - 55);
    let remainder = 0;
    for (const digit of digits) {
        remainder = (remainder * 10 + Number(digit)) % 97;
    }

    return remainder === 1;
}

export default {
    name: "ReviewRequest",
    components: { Headful, Header },
    props: {
        token: {
            type: String,
            required: true,
        },
    },
    data: () => ({
        loading: true,
        notFound: false,
        submitting: null,
        decided: false,
        decidedOutcome: null,
        request: {},
        invoice: {
            accountHolder: '',
            iban: '',
            reference: '',
        },
        requiredRule: (value) => !!value || "Dit veld is verplicht.",
        ibanRule: (value) => !value || isValidIban(value) || "Ongeldig IBAN-nummer.",
    }),
    computed: {
        formattedCosts() {
            return formatCosts(this.request.organisationCosts);
        },
        // De aanvraag kan alleen beoordeeld worden zolang die nog niet definitief is afgehandeld.
        alreadyHandled() {
            return !['submitted', 'in_conversation'].includes(this.request.status);
        },
        decidedColor() {
            if (this.decidedOutcome === 'approved') return 'success';
            if (this.decidedOutcome === 'denied') return 'error';
            return 'info';
        },
        decidedMessage() {
            if (this.decidedOutcome === 'approved') return 'De aanvraag is goedgekeurd. De aanvrager ontvangt hierover bericht.';
            if (this.decidedOutcome === 'denied') return 'De aanvraag is afgekeurd. De aanvrager ontvangt hierover bericht.';
            return 'De aanvraag is op "in gesprek" gezet. De aanvrager ontvangt hierover bericht.';
        },
    },
    mounted() {
        this.$store.dispatch('request/fetchReview', { token: this.token })
            .then(request => { this.request = request; })
            .catch(e => {
                console.error(e);
                this.notFound = true;
            })
            .finally(() => { this.loading = false; });
    },
    methods: {
        decide(status) {
            if (this.submitting) {
                return;
            }

            // De facturatiegegevens zijn alleen verplicht bij het goedkeuren.
            if (status === 'approved' && !this.$refs.invoiceForm.validate()) {
                return;
            }

            this.submitting = status;

            this.$store.dispatch('request/submitReview', { token: this.token, status, invoice: this.invoice })
                .then(() => {
                    this.decidedOutcome = status;
                    this.decided = true;
                })
                .catch(e => {
                    console.error(e);
                    // De API geeft bij een afgewezen beoordeling de reden mee (bijv. een ongeldig IBAN-nummer).
                    const error = e.response && e.response.data && e.response.data.error;
                    this.$store.dispatch('showSnackbar', { color: 'error', text: error || 'De beoordeling kon niet worden opgeslagen' });
                })
                .finally(() => { this.submitting = null; });
        },
    },
}
</script>
